import { GoogleOAuthProvider } from '@react-oauth/google';
import CryptoJS from 'crypto-js';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

// Shared
import Configs from '@/Configs';
import { KincodeButton } from '@/components/button/KincodeButton';
import { CountrySelect } from '@/components/CountrySelect/CountrySelect';
import { useToastMessage } from '@/utils/useToastMessage';

// Relative imports
import {
  SelfOnboardingWorkAccountButtons,
  type SelfOnboardingSsoDraft,
} from './SelfOnboardingWorkAccountButtons';
import { buildSelfOnboardingCountryOptions } from './core/selfOnboardingCountries';
import {
  getSelfOnboardingCampaignLanguage,
  isSelfOnboardingCampaignLanguage,
} from './core/apiCalls';
import {
  EMAIL_REGEXP,
  SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY,
  SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY,
  SELF_ONBOARDING_EMAIL_STORAGE_KEY,
  SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY,
  SELF_ONBOARDING_TICKET_STORAGE_KEY,
  SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY,
} from './core/selfOnboardingConstants';
import {
  useSelfOnboardingBranding,
  useSelfOnboardingRegister,
  useSelfOnboardingResend,
  useSelfOnboardingVerify,
} from './core/useSelfOnboardingMutations';

type FlowPhase = 'signup' | 'check-email' | 'verifying' | 'branding' | 'verify-error';

type FieldErrors = {
  companyName?: boolean;
  country?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  password?: boolean;
};

function resolveMailLanguage(lng: string): 'EN' | 'ES' {
  return lng.toLowerCase().startsWith('es') ? 'ES' : 'EN';
}

function readVerifyTokenFromLocation(): string | null {
  try {
    const token = new URL(window.location.href).searchParams.get('token');
    return token && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Required signup campaign slug from `?campaign=` (kebab-case). Missing or empty → block signup.
 */
function readCampaignSlugFromLocation(): string | null {
  try {
    const raw = new URL(window.location.href).searchParams.get('campaign');
    if (!raw) {
      return null;
    }
    const slug = raw.trim().toLowerCase();
    return slug || null;
  } catch {
    return null;
  }
}

function isSelfOnboardingRegisterSuccess(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { status?: unknown }).status === 'ok'
  );
}

function goToLogin(): void {
  window.location.assign('/');
}

function readStored(key: string): string {
  try {
    return sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function rememberFounder(founderName: string, companyName: string): void {
  try {
    sessionStorage.setItem(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY, founderName);
    sessionStorage.setItem(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY, companyName);
    sessionStorage.setItem(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY, '1');
  } catch {
    /* ignore quota */
  }
}

type SignupFormState = {
  companyName: string;
  country: string | null;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const EMPTY_SIGNUP: SignupFormState = {
  companyName: '',
  country: null,
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

/**
 * Public company self-onboarding: signup → check email → verified congratulations → login.
 */
export function NewCompanyOnboarding() {
  const { t, i18n } = useTranslation();
  const { errorToast, successToast } = useToastMessage();
  const mailLanguage = resolveMailLanguage(i18n.language || 'en');
  const countryOptions = useMemo(
    () => buildSelfOnboardingCountryOptions(i18n.language || 'en'),
    [i18n.language]
  );

  const { registerCompany, loading: registerLoading } = useSelfOnboardingRegister();
  const { resendVerification, loading: resendLoading } = useSelfOnboardingResend();
  const { verifyToken, loading: verifyLoading } = useSelfOnboardingVerify();
  const { completeBranding, loading: brandingLoading } = useSelfOnboardingBranding();

  const urlToken = useMemo(() => readVerifyTokenFromLocation(), []);
  const campaignSlug = useMemo(() => readCampaignSlugFromLocation(), []);
  const [phase, setPhase] = useState<FlowPhase>(() => (urlToken ? 'verifying' : 'signup'));
  const [localeReady, setLocaleReady] = useState(() => !campaignSlug || Boolean(urlToken));
  const [signup, setSignup] = useState<SignupFormState>(EMPTY_SIGNUP);
  const [ssoDraft, setSsoDraft] = useState<SelfOnboardingSsoDraft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [emailTaken, setEmailTaken] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [storedEmail, setStoredEmail] = useState(() => {
    try {
      return sessionStorage.getItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [brandingTicket, setBrandingTicket] = useState(() =>
    readStored(SELF_ONBOARDING_TICKET_STORAGE_KEY)
  );
  const [founderName, setFounderName] = useState(() =>
    readStored(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY)
  );
  const [companyName, setCompanyName] = useState(() =>
    readStored(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY)
  );

  const busy = registerLoading || resendLoading || verifyLoading || brandingLoading;

  useEffect(() => {
    if (urlToken || !campaignSlug) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const language = await getSelfOnboardingCampaignLanguage(campaignSlug);
        if (cancelled) return;
        if (isSelfOnboardingCampaignLanguage(language) && i18n.language !== language) {
          await i18n.changeLanguage(language);
        }
      } catch {
        /* Keep the browser locale when the campaign has no language. */
      } finally {
        if (!cancelled) setLocaleReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignSlug, i18n, urlToken]);

  useEffect(() => {
    if (!urlToken) {
      return;
    }
    let storedToken = '';
    try {
      storedToken = sessionStorage.getItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY) || '';
    } catch {
      storedToken = '';
    }
    const displayReady = readStored(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY) === '1';
    if (storedToken === urlToken && brandingTicket && displayReady) {
      setPhase('branding');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await verifyToken(urlToken);
        if (cancelled) return;
        if (isSelfOnboardingCampaignLanguage(result?.language) && i18n.language !== result.language) {
          await i18n.changeLanguage(result.language);
        }
        if (cancelled) return;
        if (result?.status === 'already_active') {
          goToLogin();
          return;
        }
        const ticket = result?.brandingTicket;
        if (typeof ticket !== 'string' || !ticket) {
          setPhase('verify-error');
          return;
        }
        try {
          sessionStorage.setItem(SELF_ONBOARDING_TICKET_STORAGE_KEY, ticket);
          sessionStorage.setItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY, urlToken);
        } catch {
          /* ignore quota */
        }
        const nextFounderName = typeof result.founderName === 'string' ? result.founderName.trim() : '';
        const nextCompanyName = typeof result.companyName === 'string' ? result.companyName.trim() : '';
        setFounderName(nextFounderName);
        setCompanyName(nextCompanyName);
        rememberFounder(nextFounderName, nextCompanyName);
        setBrandingTicket(ticket);
        setPhase('branding');
      } catch {
        if (!cancelled) {
          setPhase('verify-error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [brandingTicket, i18n, urlToken, verifyToken]);

  const handleSignupChange = useCallback((field: keyof SignupFormState, value: string | null) => {
    setSignup((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
    if (field === 'email') {
      setEmailTaken(false);
    }
    setSignupError(null);
  }, []);

  const handleSignupSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!campaignSlug) {
        return;
      }
      const errors: FieldErrors = {
        companyName: !signup.companyName.trim(),
        country: !signup.country,
        firstName: !(ssoDraft?.firstName || signup.firstName).trim(),
        lastName: !(ssoDraft?.lastName || signup.lastName).trim(),
        email: !EMAIL_REGEXP.test((ssoDraft?.email || signup.email).trim()),
        password: ssoDraft ? false : signup.password.length < 8,
      };
      setFieldErrors(errors);
      setEmailTaken(false);
      setSignupError(null);
      if (Object.values(errors).some(Boolean)) {
        return;
      }

      const email = (ssoDraft?.email || signup.email).trim().toLowerCase();
      const firstName = (ssoDraft?.firstName || signup.firstName).trim();
      const lastName = (ssoDraft?.lastName || signup.lastName).trim();
      try {
        const result = await registerCompany({
          companyName: signup.companyName.trim(),
          country: signup.country as string,
          firstName,
          lastName,
          email,
          ...(ssoDraft
            ? { signupTicket: ssoDraft.signupTicket }
            : { password: CryptoJS.HmacSHA512(signup.password, Configs.privateKey).toString() }),
          language: mailLanguage,
          campaign: campaignSlug,
        });
        if (
          result &&
          typeof result === 'object' &&
          typeof result.brandingTicket === 'string' &&
          result.brandingTicket
        ) {
          if (isSelfOnboardingCampaignLanguage(result.language) && i18n.language !== result.language) {
            await i18n.changeLanguage(result.language);
          }
          try {
            sessionStorage.setItem(SELF_ONBOARDING_TICKET_STORAGE_KEY, result.brandingTicket);
            sessionStorage.setItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY, email);
          } catch {
            /* ignore quota */
          }
          const nextFounderName = `${firstName} ${lastName}`.trim();
          const nextCompanyName = signup.companyName.trim();
          setFounderName(nextFounderName);
          setCompanyName(nextCompanyName);
          rememberFounder(nextFounderName, nextCompanyName);
          setBrandingTicket(result.brandingTicket);
          setPhase('branding');
          return;
        }
        if (result === 409) {
          setEmailTaken(true);
          return;
        }
        if (!isSelfOnboardingRegisterSuccess(result)) {
          const isClientError = typeof result === 'number' && result >= 400 && result < 500;
          setSignupError(
            campaignSlug && isClientError
              ? t('plg.selfOnboarding.error.campaignInvalid')
              : t('plg.selfOnboarding.error.generic')
          );
          return;
        }
        try {
          sessionStorage.setItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY, email);
          sessionStorage.removeItem(SELF_ONBOARDING_TICKET_STORAGE_KEY);
          sessionStorage.removeItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setStoredEmail(email);
        setPhase('check-email');
      } catch {
        setSignupError(t('plg.selfOnboarding.error.generic'));
      }
    },
    [campaignSlug, i18n, mailLanguage, registerCompany, signup, ssoDraft, t]
  );

  const handleResend = useCallback(async () => {
    if (!storedEmail) {
      errorToast(t('plg.selfOnboarding.error.resendNeedEmail'));
      return;
    }
    try {
      await resendVerification({ email: storedEmail, language: mailLanguage });
      successToast(t('plg.selfOnboarding.checkEmail.resent'));
    } catch {
      errorToast(t('plg.selfOnboarding.error.generic'));
    }
  }, [errorToast, mailLanguage, resendVerification, storedEmail, successToast, t]);

  const handleContinue = useCallback(async () => {
    if (!brandingTicket) {
      setPhase('verify-error');
      return;
    }
    try {
      await completeBranding({
        brandingTicket,
        skip: true,
      });
      try {
        sessionStorage.removeItem(SELF_ONBOARDING_TICKET_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      goToLogin();
    } catch {
      errorToast(t('plg.selfOnboarding.error.generic'));
    }
  }, [brandingTicket, completeBranding, errorToast, t]);

  const inputClass =
    'input-field h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-slate-950 shadow-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-50 dark:focus:border-indigo-400 dark:focus:bg-slate-800';
  const labelClass =
    'mb-1.5 block text-sm font-semibold tracking-tight text-slate-700 dark:text-slate-200';
  const errorClass = 'text-xs text-red-600 dark:text-red-400 mt-1';

  return (
    <GoogleOAuthProvider clientId={Configs.googleClientID}>
    <div
      data-testid="plg-company-self-onboarding"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#020617] p-4 font-sans sm:p-8 lg:p-12"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(129, 140, 248, 0.2) 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl"
      />
      <section
        data-testid="plg-new-company-onboarding-panel"
        className="relative flex w-full max-w-2xl flex-col items-center rounded-[2rem] border border-white/70 bg-white p-6 text-slate-950 shadow-kcLightOverlays dark:border-slate-700 dark:bg-slate-900 dark:text-slate-50 sm:p-8 lg:p-10"
      >
        <div
          data-testid="plg-new-company-onboarding-header"
          className="mb-6 flex w-full items-center border-b border-slate-100 pb-6 dark:border-slate-800"
        >
          <img
            className="h-8 w-auto object-contain object-center"
            alt="Kincode"
            src={Configs.companyLogoLarge}
          />
        </div>

        {phase === 'verifying' ? (
          <div className="flex min-h-64 flex-col items-center justify-center text-center">
            <div className="mb-6 h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.verifying')}
            </p>
          </div>
        ) : null}

        {phase === 'signup' && !campaignSlug ? (
          <div
            data-testid="plg-signup-campaign-required"
            className="flex w-full flex-col gap-3"
          >
            <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
              {t('plg.selfOnboarding.signup.title')}
            </h1>
            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.error.campaignRequired')}
            </p>
          </div>
        ) : null}

        {phase === 'signup' && campaignSlug && !localeReady ? (
          <div
            className="flex min-h-64 items-center justify-center"
            aria-busy="true"
            data-testid="plg-signup-locale-loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
          </div>
        ) : null}

        {phase === 'signup' && campaignSlug && localeReady ? (
          <form
            data-testid="plg-company-signup"
            className="flex w-full flex-col gap-5"
            onSubmit={handleSignupSubmit}
            noValidate
          >
            <div className="mb-1">
              <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
                {t('plg.selfOnboarding.signup.title')}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t('plg.selfOnboarding.signup.subtitle')}
              </p>
            </div>

            {ssoDraft ? (
              <div
                data-testid="plg-signup-sso-identity"
                className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-left text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <p>{t('plg.selfOnboarding.signup.ssoLocked', { email: ssoDraft.email })}</p>
                <button
                  type="button"
                  className="self-start text-sm font-medium text-indigo-700 underline dark:text-indigo-300"
                  data-testid="plg-signup-sso-clear"
                  onClick={() => setSsoDraft(null)}
                >
                  {t('plg.selfOnboarding.signup.usePassword')}
                </button>
              </div>
            ) : (
              <SelfOnboardingWorkAccountButtons
                campaignSlug={campaignSlug}
                disabled={busy}
                onDraft={(draft) => {
                  setSsoDraft(draft);
                  setSignup((prev) => ({
                    ...prev,
                    firstName: draft.firstName,
                    lastName: draft.lastName,
                    email: draft.email,
                    password: '',
                  }));
                  setEmailTaken(false);
                  setSignupError(null);
                }}
                onMessage={setSignupError}
              />
            )}

            <div>
              <label className={labelClass} htmlFor="plg-signup-company-name">
                {t('plg.selfOnboarding.fields.companyName')}
              </label>
              <input
                id="plg-signup-company-name"
                data-testid="plg-signup-company-name"
                className={inputClass}
                value={signup.companyName}
                onChange={(event) => handleSignupChange('companyName', event.target.value)}
                autoComplete="organization"
              />
              {fieldErrors.companyName ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
              ) : null}
            </div>

            <div data-testid="plg-signup-country">
              <label className={labelClass} htmlFor="plg-signup-country-input">
                {t('plg.selfOnboarding.fields.country')}
              </label>
              <CountrySelect
                inputId="plg-signup-country-input"
                options={countryOptions}
                value={signup.country}
                onChange={(code) => handleSignupChange('country', code)}
                isDisabled={busy}
              />
              {fieldErrors.country ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
              ) : null}
            </div>

            <div>
              <label className={labelClass} htmlFor="plg-signup-first-name">
                {t('plg.selfOnboarding.fields.firstName')}
              </label>
              <input
                id="plg-signup-first-name"
                data-testid="plg-signup-first-name"
                className={inputClass}
                value={signup.firstName}
                readOnly={Boolean(ssoDraft)}
                onChange={(event) => handleSignupChange('firstName', event.target.value)}
                autoComplete="given-name"
              />
              {fieldErrors.firstName ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
              ) : null}
            </div>

            <div>
              <label className={labelClass} htmlFor="plg-signup-last-name">
                {t('plg.selfOnboarding.fields.lastName')}
              </label>
              <input
                id="plg-signup-last-name"
                data-testid="plg-signup-last-name"
                className={inputClass}
                value={signup.lastName}
                readOnly={Boolean(ssoDraft)}
                onChange={(event) => handleSignupChange('lastName', event.target.value)}
                autoComplete="family-name"
              />
              {fieldErrors.lastName ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
              ) : null}
            </div>

            <div>
              <label className={labelClass} htmlFor="plg-signup-email">
                {t('plg.selfOnboarding.fields.email')}
              </label>
              <input
                id="plg-signup-email"
                data-testid="plg-signup-email"
                type="email"
                className={inputClass}
                value={signup.email}
                readOnly={Boolean(ssoDraft)}
                onChange={(event) => handleSignupChange('email', event.target.value)}
                autoComplete="email"
              />
              {fieldErrors.email ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.email')}</p>
              ) : null}
              {emailTaken ? (
                <p className={errorClass} data-testid="plg-signup-email-taken">
                  {t('plg.selfOnboarding.error.emailTaken')}
                </p>
              ) : null}
            </div>

            {ssoDraft ? null : (
            <div>
              <label className={labelClass} htmlFor="plg-signup-password">
                {t('plg.selfOnboarding.fields.password')}
              </label>
              <input
                id="plg-signup-password"
                data-testid="plg-signup-password"
                type="password"
                className={inputClass}
                value={signup.password}
                onChange={(event) => handleSignupChange('password', event.target.value)}
                autoComplete="new-password"
              />
              {fieldErrors.password ? (
                <p className={errorClass}>{t('plg.selfOnboarding.validation.password')}</p>
              ) : null}
            </div>
            )}

            {signupError ? (
              <p className={errorClass} data-testid="plg-signup-error" role="alert">
                {signupError}
              </p>
            ) : null}

            <KincodeButton
              type="submit"
              data-testid="plg-company-signup-submit"
              variant="primary"
              colorHex="#4F46E5"
              className="mt-1 w-full"
              disabled={busy}
            >
              {t('plg.selfOnboarding.signup.submit')}
            </KincodeButton>
          </form>
        ) : null}

        {phase === 'check-email' ? (
          <div
            data-testid="plg-check-email"
            className="flex min-h-64 w-full flex-col items-center justify-center gap-5 text-center"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-2xl dark:bg-indigo-500/10">
              ✉
            </div>
            <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white">
              {t('plg.selfOnboarding.checkEmail.title')}
            </h1>
            <p className="max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.checkEmail.body')}
            </p>
            <KincodeButton
              type="button"
              data-testid="plg-check-email-resend"
              variant="secondary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              disabled={busy || !storedEmail}
              onClick={() => {
                void handleResend();
              }}
            >
              {t('plg.selfOnboarding.checkEmail.resend')}
            </KincodeButton>
          </div>
        ) : null}

        {phase === 'verify-error' ? (
          <div
            data-testid="plg-verify-error"
            className="flex min-h-64 w-full flex-col items-center justify-center gap-4 text-center"
          >
            <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white">
              {t('plg.selfOnboarding.verifyError.title')}
            </h1>
            <p className="max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.verifyError.body')}
            </p>
            <KincodeButton
              type="button"
              data-testid="plg-check-email-resend"
              variant="secondary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              disabled={busy || !storedEmail}
              onClick={() => {
                void handleResend();
              }}
            >
              {t('plg.selfOnboarding.checkEmail.resend')}
            </KincodeButton>
            <KincodeButton
              type="button"
              data-testid="plg-verify-error-signup"
              variant="tertiary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              onClick={() => {
                window.location.assign(Configs.newCompanyOnboardingURL);
              }}
            >
              {t('plg.selfOnboarding.verifyError.back')}
            </KincodeButton>
          </div>
        ) : null}

        {phase === 'branding' ? (
          <div data-testid="plg-branding" className="flex w-full flex-col items-center gap-6 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300"
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none">
                <path
                  d="M6.5 12.5 10 16l7.5-8"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                {t('plg.selfOnboarding.branding.eyebrow')}
              </p>
              <h1 className="mt-3 font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
                {founderName
                  ? t('plg.selfOnboarding.branding.titleNamed', { name: founderName })
                  : t('plg.selfOnboarding.branding.title')}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {companyName
                  ? t('plg.selfOnboarding.branding.bodyWithCompany', { companyName })
                  : t('plg.selfOnboarding.branding.body')}
              </p>
            </div>
            {founderName || companyName ? (
              <dl className="w-full divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-left dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800/60">
                {founderName ? (
                  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('plg.selfOnboarding.branding.nameLabel')}
                    </dt>
                    <dd
                      className="text-sm font-semibold text-slate-950 dark:text-white"
                      data-testid="plg-branding-founder"
                    >
                      {founderName}
                    </dd>
                  </div>
                ) : null}
                {companyName ? (
                  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('plg.selfOnboarding.branding.companyLabel')}
                    </dt>
                    <dd
                      className="text-sm font-semibold text-slate-950 dark:text-white"
                      data-testid="plg-branding-company"
                    >
                      {companyName}
                    </dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
            <KincodeButton
              type="button"
              data-testid="plg-branding-continue"
              variant="primary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              disabled={busy}
              onClick={() => {
                void handleContinue();
              }}
            >
              {t('plg.selfOnboarding.branding.continue')}
            </KincodeButton>
          </div>
        ) : null}
      </section>
    </div>
    </GoogleOAuthProvider>
  );
}
